/**
 * App-wide configuration: divisions, photos, testIds, api client.
 */
import axios from "axios";

export const API_BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${API_BASE}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

/** Normalize backend error → safe string for toasts.
 * FastAPI 422 returns detail as list of {msg,loc,type,...}. React can't render objects. */
export const errMsg = (err, fallback = "Errore") => {
  const d = err?.response?.data?.detail ?? err?.response?.data ?? err?.message ?? err;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || x?.message || JSON.stringify(x)).join(" · ");
  if (d && typeof d === "object") return d.msg || d.message || fallback;
  return fallback;
};

/** Photos (local user-provided images live in /public/assets/**) */
const local = (folder, n, ext = "jpeg") =>
  Array.from({ length: n }, (_, i) => `/assets/${folder}/${folder}-${String(i + 1).padStart(2, "0")}.${ext}`);

// The first two automazione files are PNG, rest are JPEG.
const automazione = [
  "/assets/automazione/automazione-01.png",
  "/assets/automazione/automazione-02.png",
  ...local("automazione", 12).slice(2),
];
const dissalatore = [
  "/assets/dissalatore/dissalatore-hero.jpeg",
  "/assets/dissalatore/dissalatore-01.png",
  ...local("dissalatore", 4).slice(1),
];

export const photos = {
  sede: local("sede", 2),
  elettrico: local("elettrico", 11),
  automazione,
  fotovoltaico: [
    "https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1466611653911-95081537e5b7?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1548337138-e87d889cc369?auto=format&fit=crop&w=1600&q=80",
  ],
  antiincendio: [
    "/assets/antiincendio/antiincendio-01-manifold.jpg",
    "/assets/antiincendio/antiincendio-02-valves.jpg",
    "/assets/antiincendio/antiincendio-03-alarm.jpg",
  ],
  depurazione: local("depurazione", 3),
  irrigazione: local("irrigazione", 49),
  potabilizzazione: local("potabilizzazione", 5),
  dissalatore,
  laser: [
    "/assets/laser/rayzen-f3015ph.webp",
    "https://images.pexels.com/photos/29988985/pexels-photo-29988985.jpeg?auto=compress&cs=tinysrgb&w=1600",
    "https://images.pexels.com/photos/9665351/pexels-photo-9665351.jpeg?auto=compress&cs=tinysrgb&w=1600",
  ],
};

/** Combined water-treatment gallery — solo dissalatori per ora (potabilizzazione + depurazione in arrivo) */
const acquaGallery = [...dissalatore];

/** Business divisions — Irrigazione + Trattamento Acque primary,
 *  poi Automazioni, Elettrico, Fotovoltaico, Antincendio. */
export const divisions = [
  {
    id: "irrigazione",
    number: "01",
    title: "Irrigazione",
    subtitle: "Progetto · Impianto · Automazione",
    tag: "Divisione principale · Made in Italy",
    lead:
      "Impianti di irrigazione ad alta efficienza per agricoltura professionale, serre e verde urbano. Dal progetto agronomico alla centralina automatica, con controllo remoto e gestione intelligente della risorsa idrica.",
    bullets: [
      "Impianti a goccia e sub-irrigazione",
      "Centraline automatiche & sensori",
      "Pivot & rain-gun per grandi appezzamenti",
      "Filtrazione, fertirrigazione, pompaggio",
      "Telecontrollo via app / SCADA",
      "Manutenzione stagionale programmata",
    ],
    stats: [["49+", "cantieri consegnati"], ["-40%", "consumo acqua medio"], ["24/7", "assistenza"]],
    featured: true,
    hero: photos.irrigazione[39],
    gallery: [
      photos.irrigazione[39],
      photos.irrigazione[34],
      photos.irrigazione[29],
      photos.irrigazione[24],
      ...photos.irrigazione.slice(0, 8),
    ],
  },
  {
    id: "trattamento-acqua",
    number: "02",
    title: "Desalinazione & Trattamento Acque",
    subtitle: "Dissalatori · Depurazione",
    tag: "Divisione principale · Ingegneria idrica",
    lead:
      "Progettazione e realizzazione di impianti chiavi in mano per dissalare o depurare qualsiasi risorsa idrica. Osmosi inversa, ultrafiltrazione, trattamento reflui civili e industriali.",
    bullets: [
      "Dissalatori a osmosi inversa (SWRO)",
      "Depurazione reflui civili & industriali",
      "Ultrafiltrazione & MBR",
      "Analisi chimico-fisica dell'acqua",
    ],
    stats: [["10+", "impianti attivi"], ["Turn-key", "chiavi in mano"], ["100%", "Made in Italy"]],
    featured: true,
    hero: photos.dissalatore[0],
    gallery: acquaGallery.slice(0, 12),
  },
  {
    id: "automazioni",
    number: "03",
    title: "Automazioni",
    subtitle: "PLC · Robotica · Industria 4.0",
    tag: "Software & hardware su misura",
    lead:
      "Sistemi di automazione industriale su misura, dal quadro di comando alla linea di produzione integrata. Programmazione PLC, HMI, SCADA e connessione IoT/MES per l'industria 4.0.",
    bullets: [
      "Programmazione PLC Weintek · Eaton",
      "Linguaggio: Testo Strutturato (ST · IEC 61131-3)",
      "SCADA & interfacce HMI touch",
      "Automazione linee produttive",
      "Integrazione robotica ABB · Fanuc",
      "IoT · MQTT · connessione MES",
      "Retrofit & revamping macchinari esistenti",
    ],
    hero: photos.automazione[2],
    gallery: photos.automazione.slice(0, 8),
  },
  {
    id: "elettrico",
    number: "04",
    title: "Impianti Elettrici",
    subtitle: "Civili · Industriali · Speciali",
    tag: "Progettazione & installazione",
    lead:
      "Impianti elettrici certificati per abitazioni, uffici, capannoni e stabilimenti produttivi. Dalla progettazione elettrica alla quadristica di potenza, dal cablaggio strutturato all'illuminotecnica LED su misura.",
    bullets: [
      "Quadri elettrici BT & MT",
      "Cablaggio strutturato Cat 6/7",
      "Illuminotecnica industriale LED",
      "Cabine di trasformazione",
      "Rifasamento & filtri armoniche",
      "Certificazioni DM 37/08",
    ],
    hero: photos.elettrico[0],
    gallery: photos.elettrico.slice(0, 8),
  },
  {
    id: "fotovoltaico",
    number: "05",
    title: "Fotovoltaico",
    subtitle: "Solar · Storage · Ricarica EV",
    tag: "Progetto chiavi in mano",
    lead:
      "Impianti fotovoltaici residenziali, commerciali e industriali. Studio, dimensionamento, installazione, allaccio e monitoraggio: dal tetto di casa al parco energetico.",
    bullets: [
      "Dimensionamento personalizzato",
      "Sistemi di accumulo (BESS)",
      "Colonnine ricarica EV",
      "Pratiche GSE, ENEL, incentivi",
    ],
    hero: photos.fotovoltaico[0],
    gallery: photos.fotovoltaico,
  },
  {
    id: "antiincendio",
    number: "06",
    title: "Antincendio",
    subtitle: "Rivelazione · Spegnimento · Compartimentazione",
    tag: "Sicurezza & compliance",
    lead:
      "Sistemi di rivelazione e spegnimento incendi progettati secondo Codice di Prevenzione Incendi. Installazione, collaudo e manutenzione periodica.",
    bullets: [
      "Rivelazione fumo & calore",
      "Spegnimento a gas / sprinkler",
      "Evacuazione EVAC & segnaletica",
      "Manutenzione ordinaria e SCIA",
    ],
    hero: "/assets/antiincendio/antiincendio-03-alarm.jpg",
    gallery: photos.antiincendio,
  },
];

export const TID = {
  logo: "header-logo",
  navHome: "nav-home",
  navDivisioni: "nav-divisioni",
  navLaser: "nav-laser",
  navContatti: "nav-contatti",
  navAdmin: "nav-admin",
  mobileMenuBtn: "mobile-menu-btn",
  heroCtaLaser: "hero-cta-laser",
  heroCtaDivisions: "hero-cta-divisions",
  divisionCard: "division-card",
  contactName: "contact-name",
  contactEmail: "contact-email",
  contactPhone: "contact-phone",
  contactSubject: "contact-subject",
  contactMessage: "contact-message",
  contactSubmit: "contact-submit",
  laserStep1Next: "laser-step1-next",
  laserStep2Next: "laser-step2-next",
  laserStep3Next: "laser-step3-next",
  laserStep4Submit: "laser-step4-submit",
  laserMaterial: "laser-material",
  laserThickness: "laser-thickness",
  laserWidth: "laser-width",
  laserHeight: "laser-height",
  laserQuantity: "laser-quantity",
  laserPostal: "laser-postal",
  laserCutLength: "laser-cut-length",
  laserDwg: "laser-dwg",
  laserCustName: "laser-cust-name",
  laserCustEmail: "laser-cust-email",
  laserCustPhone: "laser-cust-phone",
  laserCustCompany: "laser-cust-company",
  laserCustNotes: "laser-cust-notes",
  adminEmail: "admin-email",
  adminPassword: "admin-password",
  adminSubmit: "admin-submit",
  adminLogout: "admin-logout",
  adminTabQuotes: "admin-tab-quotes",
  adminTabMaterials: "admin-tab-materials",
  adminTabContacts: "admin-tab-contacts",
};
